import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { ThumbUp, ThumbDown } from '@/assets/icons';
import editPencilIcon from '@/assets/images/patient-info-edit-pencil.png';
import durationIcon from '@/assets/images/patient-info-duration.png';
import modeOfOnsetIcon from '@/assets/images/patient-info-mode-of-onset.png';
import addictionAlcoholIcon from '@/assets/images/patient-info-addiction-alcohol.png';
import addictionSmokingIcon from '@/assets/images/patient-info-addiction-smoking.png';
import addictionSyringeIcon from '@/assets/images/patient-info-addiction-syringe.png';
import { summarizeChiefComplaint } from '@/ayurveda/utils/parseAyurveda';
import type { PersonalHistory } from '@/ayurveda/types/ayurveda';

/**
 * Which addiction icons to highlight, parsed from the free-text
 * `personalHistory.addiction` string (e.g. "Alcohol and smoking, both
 * occasionally", "Smoking, 5 cigarettes/day", "None"). Both the kiosk agent
 * and the live CDSS agent are prompted to always name the specific
 * substance(s) explicitly for exactly this reason — a vague value with no
 * substance named highlights nothing, by design, rather than guessing.
 */
function getHighlightedAddictions(addiction?: string | null): {
  alcohol: boolean;
  smoking: boolean;
  drugs: boolean;
} {
  // "Non-smoker", "no alcohol", "denies tobacco" name a substance but mean the
  // opposite — drop negated clauses before matching. Clauses split on , ; . and
  // "but"/"and" so "no alcohol, smokes daily" still highlights smoking.
  const text = (addiction || '')
    .toLowerCase()
    .split(/[,;.\n]|\bbut\b|\band\b/)
    .filter(
      (clause) =>
        !/\b(no|non|not|never|nil|none|denies|denied|negative|without|quit|stopped)\b|\bnon-/.test(
          clause
        )
    )
    .join(' ');
  return {
    alcohol: /alcohol|drink|wine|beer|whisky|whiskey|rum|liquor/.test(text),
    smoking: /smok|cigar|tobacco|beedi|bidi/.test(text),
    // Generic terms (drug(s)/inject/narcotic) plus specific substance names —
    // the agent is prompted to name the actual substance (e.g. "cocaine",
    // "morphine") rather than say "drugs", so the generic terms alone miss
    // most real answers.
    drugs:
      /\bdrugs?\b|inject|narcotic|cocaine|heroin|morphine|opium|opioid|marijuana|cannabis|\bweed\b|ganja|hashish|charas|methamphetamine|\bmeth\b|mdma|ecstasy|\blsd\b|ketamine|smack|brown sugar/.test(
        text
      ),
  };
}

export interface PatientInformationCardProps {
  chiefComplaint?: string | null;
  duration?: string | null;
  modeOfOnset?: string | null;
  history?: string | null;
  pastIllness?: string | null;
  treatmentHistory?: string | null;
  historyFamilyHistory?: string | null;
  currentMedication?: string | null;
  familySocialOccupational?: string | null;
  personalHistory?: PersonalHistory | null;
}

const PERSONAL_HISTORY_LABELS: Array<{ key: keyof PersonalHistory; label: string }> = [
  { key: 'diet', label: 'Diet' },
  { key: 'appetite', label: 'Appetite' },
  { key: 'sleep', label: 'Sleep' },
  { key: 'exercise', label: 'Exercise' },
  { key: 'bowel', label: 'Bowel' },
  { key: 'bladder', label: 'Bladder' },
];

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-1.5">
      <span
        aria-hidden="true"
        className="mt-[7px] h-[4px] w-[4px] flex-shrink-0 rounded-full bg-[#585859]"
      />
      <span>{children}</span>
    </li>
  );
}

function FieldWithIcon({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
}) {
  return (
    <div className="flex items-start gap-1">
      {icon}
      <div className="flex flex-col text-[#585859]">
        <span className="font-hanken text-base font-medium">{label}</span>
        <span className="font-hanken text-sm">{value || 'No data available'}</span>
      </div>
    </div>
  );
}

function PersonalHistoryField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col text-[#585859]">
      <p className="font-hanken text-base font-semibold leading-tight">{label}:</p>
      <p className="font-hanken text-base leading-tight">{value || '—'}</p>
    </div>
  );
}

export function PatientInformationCard({
  chiefComplaint,
  duration,
  modeOfOnset,
  history,
  pastIllness,
  treatmentHistory,
  historyFamilyHistory,
  currentMedication,
  familySocialOccupational,
  personalHistory,
}: PatientInformationCardProps) {
  const [isOpen, setIsOpen] = useState(true);

  const complaints = summarizeChiefComplaint(chiefComplaint);

  const historyFields: Array<{ label: string; value?: string | null }> = [
    { label: 'History of Past Illness', value: pastIllness },
    { label: 'Treatment History Of', value: treatmentHistory },
    { label: 'Family History Of', value: historyFamilyHistory },
    { label: 'Current Medication', value: currentMedication },
    { label: 'Family/ Social/ Occupational', value: familySocialOccupational },
  ];
  const hasHistoryFields = historyFields.some(({ value }) => Boolean(value)) || Boolean(history);
  const highlightedAddictions = getHighlightedAddictions(personalHistory?.addiction);

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-2xl bg-[#F8F8F1] p-4">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <img src={editPencilIcon} alt="" className="h-[18px] w-[16px]" aria-hidden="true" />
          <h2 className="font-hanken text-[calc(20px*var(--app-scale))] font-medium leading-6 tracking-[0px] text-[#3C4F1E]">
            Patient&rsquo;s Reported Information
          </h2>
        </div>
        {isOpen ? (
          <ChevronUp size={18} className="text-[#8D90A1]" />
        ) : (
          <ChevronDown size={18} className="text-[#8D90A1]" />
        )}
      </button>

      {isOpen && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1 rounded-xl bg-white px-3 py-1">
            <p className="font-hanken text-lg font-bold text-[#585859]">Chief Complaint:</p>
            {complaints.length > 0 ? (
              <ul className="flex flex-col gap-1 text-sm font-normal leading-5 text-[#585859]">
                {complaints.map((complaint) => (
                  <Bullet key={complaint}>{complaint}</Bullet>
                ))}
              </ul>
            ) : (
              <p className="text-sm italic text-muted-foreground">No data available</p>
            )}

            <div className="flex items-start justify-between gap-8 py-1">
              <FieldWithIcon
                icon={<img src={durationIcon} alt="" className="h-[28px] w-5" aria-hidden="true" />}
                label="Duration"
                value={duration}
              />
              <FieldWithIcon
                icon={
                  <img src={modeOfOnsetIcon} alt="" className="h-[28px] w-5" aria-hidden="true" />
                }
                label="Mode of Onset"
                value={modeOfOnset}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1 rounded-xl bg-white px-3 py-1">
            <p className="font-hanken text-lg font-bold text-[#585859]">History:</p>
            {hasHistoryFields ? (
              <ul className="flex flex-col gap-1 text-sm font-normal leading-5 text-[#585859]">
                {historyFields.map(({ label, value }) => (
                  <Bullet key={label}>
                    <span className="font-semibold">{label}:</span> {value || ''}
                  </Bullet>
                ))}
              </ul>
            ) : (
              <p className="text-sm italic text-muted-foreground">No data available</p>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-xl bg-white px-3 pb-1.5 pt-1">
            <p className="font-hanken text-lg font-bold text-[#585859]">Personal History:</p>
            <div className="grid grid-cols-2 gap-x-10 gap-y-2">
              {PERSONAL_HISTORY_LABELS.map(({ key, label }) => (
                <PersonalHistoryField key={key} label={label} value={personalHistory?.[key]} />
              ))}
              <div className="flex flex-col text-[#585859]">
                <div className="flex items-center gap-1.5">
                  <span className="font-hanken text-base font-semibold">Addiction:</span>
                  {/* Icons are highlighted via a colored badge (background + ring),
                      not opacity/grayscale alone — the smoking/syringe PNGs are
                      already near-white by design, so toggling their opacity is
                      not visible on its own. The badge is visible regardless of
                      the icon's native color. */}
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
                      highlightedAddictions.alcohol
                        ? 'bg-red-100 ring-1 ring-red-300'
                        : 'bg-transparent'
                    }`}
                  >
                    <img
                      src={addictionAlcoholIcon}
                      alt=""
                      title="Alcohol"
                      className={`h-[16px] w-[16px] transition-opacity ${
                        highlightedAddictions.alcohol ? 'opacity-100' : 'opacity-25 grayscale'
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
                      highlightedAddictions.smoking
                        ? 'bg-orange-100 ring-1 ring-orange-300'
                        : 'bg-transparent'
                    }`}
                  >
                    <img
                      src={addictionSmokingIcon}
                      alt=""
                      title="Smoking"
                      className={`h-[18px] w-[18px] transition-opacity ${
                        highlightedAddictions.smoking ? 'opacity-100' : 'opacity-25 grayscale'
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
                      highlightedAddictions.drugs
                        ? 'bg-purple-100 ring-1 ring-purple-300'
                        : 'bg-transparent'
                    }`}
                  >
                    <img
                      src={addictionSyringeIcon}
                      alt=""
                      title="Drugs"
                      className={`h-[16px] w-[16px] transition-opacity ${
                        highlightedAddictions.drugs ? 'opacity-100' : 'opacity-25 grayscale'
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                </div>
                <p className="font-hanken text-base leading-tight">
                  {personalHistory?.addiction || '—'}
                </p>
              </div>
              <PersonalHistoryField label="Menstrual" value={personalHistory?.menstrual} />
            </div>
          </div>

          <div className="flex items-center gap-2 px-1">
            <ThumbUp size={16} fill="#717072" />
            <ThumbDown size={16} fill="#717072" />
          </div>
        </div>
      )}
    </div>
  );
}
